import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import JobsTable from './jdTable';
import CandidateFollowup from './Cc';
import { useAuth } from '../../../context/AuthContext';
import './ViewJobs.css';

function AllJob({ selectedCompany, setActiveMain, setSelectedJob }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { isSuperAdmin, isAdminAuthenticated } = useAuth();
    const isNormalAdmin = isAdminAuthenticated && !isSuperAdmin;
    
    const jobID = new URLSearchParams(location.search).get("jobID");
    const companyID = new URLSearchParams(location.search).get("companyID");
    const [companyEmail, setCompanyEmail] = useState(""); // State to hold company email
    const [selectedJobId, setSelectedJobId] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const jobsContainerRef = useRef(null);
    const [jobsSectionHeight, setJobsSectionHeight] = useState(0);
    const [loading, setLoading] = useState(true);
    const [jobs, setJobs] = useState([]);

    const fetchCompanyByID = async (companyID) => {
        try {
            console.log("companyID", companyID);
            const response = await fetch(`/api/get_company_by_ID?companyID=${encodeURIComponent(companyID)}`, {
                method: "GET",
                // headers: { "Content-Type": "application/json" },
            });
            if (!response.ok) {
                throw new Error("Failed to fetch company");
            }
            const data = await response.json();
            console.log("Company Data:", data);
            setCompanyEmail(data.email); // Set the company email from the fetched data
            return data;
        } catch (error) {
            console.error("Error fetching company:", error);
            return null;
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            await fetchCompanyByID(companyID);
        };
        if (companyID) fetchData();
    }, [companyID]);

    useEffect(() => {
        const fetchJobs = async () => {
            if (!companyEmail) {
                return; // Wait for company email to be set
            }
            
            try {
                const url = `/api/get_jd?company_email=${encodeURIComponent(companyEmail)}`;
                const response = await fetch(url, {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                });

                if (!response.ok) {
                    throw new Error("Failed to fetch JDs");
                }

                const data = await response.json();
                setJobs(data);
            } catch (error) {
                console.error("Error fetching jobs:", error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchJobs();
    }, [companyEmail]);

    useEffect(() => {
        if (!jobsContainerRef.current) return;

        // Set initial height
        setJobsSectionHeight(jobsContainerRef.current.offsetHeight);

        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                setJobsSectionHeight(entry.contentRect.height);
            }
        });

        resizeObserver.observe(jobsContainerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [jobs]); // 👈 Now recalculates when jobs update

    // Extra recalculation after loading in case ResizeObserver misses it
    useEffect(() => {
        if (!loading && jobsContainerRef.current) {
            setTimeout(() => {
                setJobsSectionHeight(jobsContainerRef.current.offsetHeight);
            }, 100);
        }
    }, [loading]);

    const toggleDropdown = (e) => {
        e.stopPropagation();
        setShowDropdown(prev => !prev);
    };

    const handleOutsideClick = (e) => {
        if (!e.target.closest('.admin-viewjobs-postjob-wrapper')) {
            setShowDropdown(false);
        }
    };

    const handleCreateJD = () => {
        navigate(`/create-JD?companyID=${encodeURIComponent(companyID)}`);
        setShowDropdown(false);
    };

    const handleGenerateJD = () => {
        navigate('/jdai');
        setShowDropdown(false);
    };

    const handleUploadJD = () => {
        navigate(`/upload-JD?companyID=${encodeURIComponent(companyID)}`);
        setShowDropdown(false);
    };

    const handleClosePopup = () => {
        setShowPopup(false);
    };

    useEffect(() => {
        document.addEventListener('click', handleOutsideClick);
        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, []);

    const handleJobSelect = (jobId) => {
        setSelectedJobId(jobId);
    };

    if (loading) {
        return (
            <div className="admin-viewjobs-loader-wrapper">
                <div className="admin-viewjobs-spinner"></div>
            </div>
        );
    }

    return (
        <>
            <div className="page-container">
                    
                    <div className="dashboard-container">
                        <section className="ddashboardd">
                            <div className="admin-viewjobs-main-container" ref={jobsContainerRef}>
                                <div className="admin-viewjobs-wrapper-section">
                                    <div className={isNormalAdmin ? "admin-viewjobs-top-header-readonly" : "admin-viewjobs-top-header"}>
                                        <div className="admin-viewjobs-title-area">
                                            <span className="admin-viewjobs-title-text">{isNormalAdmin ? 'My Assigned Jobs' : 'All Jobs'}</span>
                                            <img
                                                className="admin-viewjobs-title-icon"
                                                loading="lazy"
                                                src="https://cdn.builder.io/api/v1/image/assets/TEMP/2921160c9100cfc9e9704213dae0ba0daecd34621dab47297f1e437d36bd0640?placeholderIfAbsent=true&apiKey=1de991be0a104079ba790b2b7061fba4"
                                                alt=""
                                            />
                                        </div>

                                        {!isNormalAdmin && (
                                            <div className="admin-viewjobs-postjob-wrapper" onClick={(e) => e.stopPropagation()}>
                                                <button className="admin-viewjobs-postjob-button" onClick={toggleDropdown}>
                                                    Post Job ▾
                                                </button>

                                                {showDropdown && (
                                                    <div className="admin-viewjobs-postjob-menu">
                                                        <button className="admin-viewjobs-postjob-menuitem" onClick={handleCreateJD}>
                                                            Create JD
                                                        </button>
                                                        <button className="admin-viewjobs-postjob-menuitem" onClick={handleGenerateJD}>
                                                            Generate JD with AI
                                                        </button>
                                                        <button className="admin-viewjobs-postjob-menuitem" onClick={handleUploadJD}>
                                                            Upload JD
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* {showPopup && <UploadPopup onClose={handleClosePopup} onUpload={handleUploadJD} />} */}
                                    </div>

                                    <div>
                                        <JobsTable
                                            selectedCompany={selectedCompany}
                                            onJobSelect={handleJobSelect}
                                            setSelectedJob={setSelectedJob}
                                            setActiveMain={setActiveMain}
                                            companyEmail={companyEmail}
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* <div className="djobs-all-jobs">
                                <CandidateFollowup maxHeight={jobsSectionHeight} companyEmail={companyEmail} />
                            </div> */}
                        </section>
                    </div>
            </div>
        </>
    );
}

export default AllJob;
